/**
 * Modern Web Crypto API implementation for QKey
 * Replaces crypto-js with native browser crypto
 */

import { sha256 } from '@noble/hashes/sha256'
import { sha3_256 } from '@noble/hashes/sha3'
import type { QuantumKeyPair, QuantumSignature, CryptoResult } from '@/types/quantum'

export class WebCrypto {
  private static instance: WebCrypto
  
  static getInstance(): WebCrypto {
    if (!WebCrypto.instance) {
      WebCrypto.instance = new WebCrypto()
    }
    return WebCrypto.instance
  }

  /**
   * Generate cryptographically secure random bytes
   */
  generateRandomBytes(length: number): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(length))
  }

  /**
   * SHA-256 hash using Noble library
   */
  sha256(data: Uint8Array | string): Uint8Array {
    if (typeof data === 'string') {
      data = new TextEncoder().encode(data)
    }
    return sha256(data)
  }

  /**
   * Generic hash method (defaults to SHA-256)
   */
  async hash(data: string): Promise<string> {
    const bytes = new TextEncoder().encode(data)
    const hashBytes = this.sha256(bytes)
    return Array.from(hashBytes).map(b => b.toString(16).padStart(2, '0')).join('')
  }

  /**
   * SHA3-256 hash using Noble library
   */
  sha3_256(data: Uint8Array | string): Uint8Array {
    if (typeof data === 'string') {
      data = new TextEncoder().encode(data)
    }
    return sha3_256(data)
  }

  /**
   * RIPEMD-160 hash (simplified implementation)
   * Note: For full Bitcoin compatibility, consider importing a dedicated library
   */
  async ripemd160(data: Uint8Array): Promise<Uint8Array> {
    // This is a placeholder - in production, use a proper RIPEMD-160 implementation
    // For now, we'll use a truncated SHA-256
    const hash = this.sha256(data)
    return hash.slice(0, 20) // Truncate to 160 bits (20 bytes)
  }

  /**
   * PBKDF2 key derivation using Web Crypto API
   */
  async pbkdf2(
    password: string,
    salt: Uint8Array,
    iterations: number,
    keyLength: number
  ): Promise<Uint8Array> {
    const passwordBuffer = new TextEncoder().encode(password)
    
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    )

    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt as BufferSource,
        iterations,
        hash: 'SHA-256',
      },
      keyMaterial,
      keyLength * 8
    )

    return new Uint8Array(derivedBits)
  }

  /**
   * AES-256-GCM encryption using Web Crypto API
   */
  async aesEncrypt(
    data: Uint8Array,
    password: string
  ): Promise<{ encrypted: Uint8Array; salt: Uint8Array; iv: Uint8Array }> {
    const salt = this.generateRandomBytes(32)
    const iv = this.generateRandomBytes(12)
    
    // Derive key from password
    const key = await this.pbkdf2(password, salt, 100000, 32)
    
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      key as BufferSource,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    )

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      cryptoKey,
      data as BufferSource
    )

    return {
      encrypted: new Uint8Array(encrypted),
      salt,
      iv,
    }
  }

  /**
   * AES-256-GCM decryption using Web Crypto API
   */
  async aesDecrypt(
    encryptedData: Uint8Array,
    password: string,
    salt: Uint8Array,
    iv: Uint8Array
  ): Promise<Uint8Array> {
    // Derive key from password
    const key = await this.pbkdf2(password, salt, 100000, 32)
    
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      key as BufferSource,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    )

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      cryptoKey,
      encryptedData as BufferSource
    )

    return new Uint8Array(decrypted)
  }

  /**
   * Generate ECDSA key pair for classical signatures
   */
  async generateECDSAKeyPair(): Promise<CryptoKeyPair> {
    return await crypto.subtle.generateKey(
      {
        name: 'ECDSA',
        namedCurve: 'P-256',
      },
      true,
      ['sign', 'verify']
    )
  }

  /**
   * Sign data with ECDSA
   */
  async ecdsaSign(data: Uint8Array, privateKey: CryptoKey): Promise<Uint8Array> {
    const signature = await crypto.subtle.sign(
      {
        name: 'ECDSA',
        hash: 'SHA-256',
      },
      privateKey,
      data as BufferSource
    )

    return new Uint8Array(signature)
  }

  /**
   * Verify ECDSA signature
   */
  async ecdsaVerify(
    signature: Uint8Array,
    data: Uint8Array,
    publicKey: CryptoKey
  ): Promise<boolean> {
    return await crypto.subtle.verify(
      {
        name: 'ECDSA',
        hash: 'SHA-256',
      },
      publicKey,
      signature as BufferSource,
      data as BufferSource
    )
  }

  /**
   * Convert hex string to Uint8Array
   */
  hexToBytes(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16)
    }
    return bytes
  }

  /**
   * Convert Uint8Array to hex string
   */
  bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
  }

  /**
   * Convert Uint8Array to Base64
   */
  bytesToBase64(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...bytes))
  }

  /**
   * Convert Base64 to Uint8Array
   */
  base64ToBytes(base64: string): Uint8Array {
    const binaryString = atob(base64)
    return new Uint8Array(binaryString.length).map((_, i) => 
      binaryString.charCodeAt(i)
    )
  }

  /**
   * Simple encrypt method for wallet-security compatibility
   */
  async encrypt(data: string, password: string): Promise<string> {
    const result = await this.aesEncrypt(
      new TextEncoder().encode(data),
      password
    )
    // Combine salt, iv, and encrypted data
    const combined = new Uint8Array(result.salt.length + result.iv.length + result.encrypted.length)
    combined.set(result.salt, 0)
    combined.set(result.iv, result.salt.length)
    combined.set(result.encrypted, result.salt.length + result.iv.length)
    return this.bytesToBase64(combined)
  }

  /**
   * Simple decrypt method for wallet-security compatibility
   */
  async decrypt(encryptedData: string, password: string): Promise<string> {
    const combined = this.base64ToBytes(encryptedData)
    const salt = combined.slice(0, 32)
    const iv = combined.slice(32, 44)
    const encrypted = combined.slice(44)
    
    const decrypted = await this.aesDecrypt(
      encrypted,
      password,
      salt,
      iv
    )
    return new TextDecoder().decode(decrypted)
  }

  /**
   * Secure memory clearing (best effort)
   */
  clearMemory(data: Uint8Array): void {
    data.fill(0)
  }

  /**
   * Constant-time comparison to prevent timing attacks
   */
  constantTimeCompare(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false
    
    let result = 0
    for (let i = 0; i < a.length; i++) {
      result |= a[i] ^ b[i]
    }
    return result === 0
  }

}

// Export singleton instance
export const webCrypto = WebCrypto.getInstance()